import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Truck, Globe, Euro, Clock, Save, Power, Zap } from "lucide-react";

export default function VendorShippingConfig() {
    const [cfg, setCfg] = useState(null);
    const [countries, setCountries] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [types, setTypes] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/vendor/shipping-config"),
            api.get("/shipping/countries"),
            api.get("/carriers"),
            api.get("/shipping/types"),
        ]).then(([c, p, cr, t]) => {
            setCfg(c.data);
            setCountries(p.data);
            setCarriers(cr.data);
            setTypes(t.data);
        });
    }, []);

    async function save() {
        setSaving(true);
        try {
            const r = await api.put("/vendor/shipping-config", cfg);
            setCfg(r.data);
            toast.success("Configuração de envio guardada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    }

    function toggleCountry(code) {
        const arr = cfg.countries.includes(code)
            ? cfg.countries.filter((c) => c !== code)
            : [...cfg.countries, code];
        setCfg({ ...cfg, countries: arr });
    }

    function toggleType(code) {
        const arr = (cfg.shipping_types || []).includes(code)
            ? cfg.shipping_types.filter((t) => t !== code)
            : [...(cfg.shipping_types || []), code];
        setCfg({ ...cfg, shipping_types: arr });
    }

    if (!cfg) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    return (
        <section className="space-y-6" data-testid="vendor-shipping-config">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <span className="label-overline text-wine/60">Logística</span>
                    <h1 className="font-heading text-3xl text-wine-dark">Configuração de envio</h1>
                    <p className="text-sm text-wine-dark/60 mt-1">Define como envias as tuas encomendas. A plataforma não realiza envio próprio — tu controlas embalagem, transportadora e tracking.</p>
                </div>
                <button onClick={save} disabled={saving} className="btn-wine text-xs px-5 py-2.5 inline-flex items-center gap-2" data-testid="save-shipping-config">
                    <Save className="h-3.5 w-3.5" /> {saving ? "A guardar…" : "Guardar"}
                </button>
            </div>

            <Card icon={Power} title="Estado">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={cfg.enabled}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                        className="accent-wine w-4 h-4"
                        data-testid="config-enabled"
                    />
                    <div>
                        <span className="text-sm text-wine-dark font-semibold">Aceitar encomendas e enviar produtos</span>
                        <p className="text-xs text-wine-dark/60">Desativar pausa a tua presença no marketplace — os teus produtos continuam visíveis mas não permitem checkout.</p>
                    </div>
                </label>
            </Card>

            <Card icon={Globe} title="Países atendidos">
                <p className="text-xs text-wine-dark/60 mb-4">Seleciona para onde aceitas enviar. O cliente só consegue concluir o pedido se enviares para o país de entrega dele.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {countries.map((c) => {
                        const on = cfg.countries.includes(c.code);
                        return (
                            <button
                                key={c.code}
                                onClick={() => toggleCountry(c.code)}
                                className={`px-3 py-2.5 rounded-md text-xs text-left border transition-all ${on ? "bg-wine text-cream border-wine" : "bg-white text-wine-dark border-wine-dark/10 hover:border-wine/40"}`}
                                data-testid={`country-${c.code}`}
                            >
                                <div className="font-semibold">{c.name}</div>
                                <div className={`text-[10px] ${on ? "text-gold-soft" : "text-wine-dark/50"}`}>{c.code}</div>
                            </button>
                        );
                    })}
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card icon={Euro} title="Preço base">
                    <NumField
                        label="Preço de envio padrão (€)"
                        value={cfg.base_price}
                        onChange={(v) => setCfg({ ...cfg, base_price: parseFloat(v) || 0 })}
                        step="0.01"
                        testId="base-price"
                    />
                    <NumField
                        label="Frete grátis a partir de (€)"
                        value={cfg.free_threshold}
                        onChange={(v) => setCfg({ ...cfg, free_threshold: parseFloat(v) || 0 })}
                        step="1"
                        testId="free-threshold"
                        hint="Pedidos acima deste valor têm portes grátis para o cliente."
                    />
                </Card>

                <Card icon={Clock} title="Prazos (dias úteis)">
                    <NumField
                        label="Tempo de preparação (dias)"
                        value={cfg.prep_days}
                        onChange={(v) => setCfg({ ...cfg, prep_days: parseInt(v) || 0 })}
                        step="1"
                        testId="prep-days"
                        hint="Tempo desde a confirmação do pagamento até o envio sair."
                    />
                    <NumField
                        label="Tempo de entrega (dias)"
                        value={cfg.delivery_days}
                        onChange={(v) => setCfg({ ...cfg, delivery_days: parseInt(v) || 0 })}
                        step="1"
                        testId="delivery-days"
                        hint="Tempo desde a recolha pela transportadora até a entrega."
                    />
                </Card>
            </div>

            <Card icon={Zap} title="Tipos de envio aceites">
                <p className="text-xs text-wine-dark/60 mb-4">O cliente escolhe o tipo de envio no checkout. Expresso/Premium têm preços majorados automaticamente.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {types.map((t) => {
                        const on = (cfg.shipping_types || []).includes(t.code);
                        return (
                            <button
                                key={t.code}
                                onClick={() => toggleType(t.code)}
                                className={`p-4 rounded-md text-left border transition-all ${on ? "border-wine bg-wine/5" : "border-wine-dark/10 bg-white hover:border-wine/30"}`}
                                data-testid={`type-${t.code}`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-heading text-base text-wine-dark">{t.name}</span>
                                    {on && <span className="label-overline text-[9px] text-wine">Ativo</span>}
                                </div>
                                <p className="text-xs text-wine-dark/55">×{t.multiplier} preço · {t.extra_days >= 0 ? "+" : ""}{t.extra_days} dias</p>
                            </button>
                        );
                    })}
                </div>
            </Card>

            <Card icon={Truck} title="Transportadora padrão">
                <p className="text-xs text-wine-dark/60 mb-4">Será pré-selecionada nos novos envios. Podes alterar caso a caso.</p>
                <select
                    value={cfg.default_carrier || ""}
                    onChange={(e) => setCfg({ ...cfg, default_carrier: e.target.value })}
                    className="w-full md:w-1/2 px-3 py-2.5 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                    data-testid="default-carrier"
                >
                    {carriers.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                </select>
            </Card>

            <div className="bg-gold-soft/10 border border-gold-soft/40 rounded-md p-4 text-xs text-wine-dark/70">
                <strong className="text-wine">Lembra-te:</strong> o Caseirinho.pt não realiza envio próprio. És responsável por contratar a transportadora, embalar e adicionar o tracking no painel de envios. A plataforma centraliza a comunicação com o cliente e calcula o frete com base nesta configuração.
            </div>
        </section>
    );
}

function Card({ icon: Icon, title, children }) {
    return (
        <div className="bg-white rounded-md border border-gold-soft/30 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
                <Icon className="h-4 w-4 text-wine" strokeWidth={1.5} />
                <h2 className="font-heading text-xl text-wine-dark">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function NumField({ label, value, onChange, step, hint, testId }) {
    return (
        <label className="block mb-3 last:mb-0">
            <span className="label-overline text-wine-dark/60 mb-1.5 block">{label}</span>
            <input
                type="number"
                step={step}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full md:w-1/2 px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                data-testid={testId}
            />
            {hint && <p className="text-[11px] text-wine-dark/55 mt-1">{hint}</p>}
        </label>
    );
}
